/**
 * @fileoverview Custom hook for fetching subscription usage data.
 */

import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

/**
 * Fetches the current user's subscription usage stats.
 *
 * @param {boolean} [enabled=true] When false, skips the fetch entirely and
 *   reports usage as null. Callers pass subscriptionsEnabled(user): with the
 *   paywall off there is no quota to enforce and no meter rendered, so the
 *   request would only be a wasted round-trip on every Settings mount.
 * @returns {{ usage: object|null, loading: boolean, refetch: function }}
 */
const useUsage = (enabled = true) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(enabled);

  const fetchUsage = useCallback(async (signal) => {
    setLoading(true);
    try {
      const response = await api.get("/subscription/usage", signal ? { signal } : {});
      setUsage(response.data);
    } catch (error) {
      if (signal?.aborted) return;
      console.error("Failed to fetch usage:", error);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setUsage(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    fetchUsage(controller.signal);
    return () => controller.abort();
  }, [fetchUsage, enabled]);

  return { usage, loading, refetch: fetchUsage };
};

export default useUsage;
